import React, { useEffect, useState } from 'react'
import { Heart, WishlistWhite } from '../../assests/ExportImageIcon'
import Modal from '../modal/Modal'
import service from '../../services/service'
import endpoints from '../../services/endpoints'

// Tapping the heart saves straight to the user's individual wishlist. If the
// user also owns collaborative wishlists, a modal lets them pick where to
// save instead — per the fav flow: default individual, prompt only when a
// collaboration exists.
//
// `item` identifies what's being saved — { recordModule, recordId, recordName,
// productId, imageUrl, provider }. `provider` should be the search API's own
// service_provider value (e.g. "benzy") — falls back to "self" if the calling
// page doesn't have one to pass. Pages that don't have real backend item data
// yet (e.g. cruiseResults.jsx, still static/mock) can omit `item` entirely:
// the button still toggles visually but skips the POST, matching the previous
// local-only behaviour rather than saving garbage records.
//
// `preSavedItemId`/`preSavedWishlistId` — pass these when the parent page
// already knows (from useIndividualWishlistItems) that this exact item is
// already in the user's individual wishlist, so the heart renders filled from
// first paint instead of every card starting blank on each fresh page load —
// tapping an already-saved item otherwise hits the backend's "This item is
// already in the wishlist" 400 with no prior visual cue.
const SaveToWishlistButton = ({ className = 'hotelCard__wishlist', item = null, preSavedItemId = null, preSavedWishlistId = null }) => {
    const [saved, setSaved] = useState(Boolean(preSavedItemId && preSavedWishlistId))
    const [open, setOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [saving, setSaving] = useState(false)
    const [collaborations, setCollaborations] = useState(null)
    const [individualId, setIndividualId] = useState(preSavedWishlistId)
    const [selected, setSelected] = useState('individual')
    // Which wishlist/item this got saved as, so a second tap can actually
    // un-fav it via DELETE instead of being a no-op.
    const [savedWishlistId, setSavedWishlistId] = useState(preSavedWishlistId)
    const [savedItemId, setSavedItemId] = useState(preSavedItemId)

    // The parent's wishlist-items lookup often resolves after this button has
    // already mounted (it's one shared fetch for the whole results page) — sync
    // in when it lands, but don't clobber a state the user already changed here.
    useEffect(() => {
        if (preSavedItemId && preSavedWishlistId && !saved) {
            setSaved(true)
            setSavedItemId(preSavedItemId)
            setSavedWishlistId(preSavedWishlistId)
            setIndividualId((prev) => prev ?? preSavedWishlistId)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preSavedItemId, preSavedWishlistId])

    const saveToWishlist = async (wishlistId) => {
        if (!item || !wishlistId) {
            setSaved(true)
            return
        }
        setSaving(true)
        try {
            // Backend caps record_id/product_id at 100 chars — some sources (e.g.
            // car search's reference_id) are long session-scoped tokens that blow
            // past that, so clip defensively regardless of which module it's from.
            const clip = (value) => String(value ?? '').slice(0, 100)
            const { data } = await service.post(endpoints.wishlists.itemsCreate.replace('{wishlist_id}', wishlistId), [{
                provider: item.provider || 'self',
                record_module: item.recordModule,
                record_id: clip(item.recordId),
                record_name: item.recordName,
                product_id: clip(item.productId ?? item.recordId),
                image_url: item.imageUrl || '',
            }], { silent: true })
            const created = Array.isArray(data?.data) ? data.data[0] : data?.data
            setSavedWishlistId(wishlistId)
            setSavedItemId(created?.id ?? null)
            setSaved(true)
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setSaving(false)
        }
    }

    const removeFromWishlist = async () => {
        if (!item || !savedWishlistId || !savedItemId) {
            // Nothing real was ever saved (no item prop, or the create response
            // didn't echo an id) — just flip the visual state back.
            setSaved(false)
            return
        }
        setSaving(true)
        try {
            await service.delete(
                endpoints.wishlists.itemDelete.replace('{wishlist_id}', savedWishlistId).replace('{item_id}', savedItemId),
            )
            setSaved(false)
            setSavedWishlistId(null)
            setSavedItemId(null)
        } catch {
            // error toast already shown by the service interceptor
        } finally {
            setSaving(false)
        }
    }

    const handleClick = async (e) => {
        e.stopPropagation()
        if (loading || saving) return

        if (saved) {
            await removeFromWishlist()
            return
        }

        if (collaborations === null) {
            setLoading(true)
            try {
                const { data } = await service.get(endpoints.wishlists.list, {}, { silent: true })
                // /wishlists/ returns a flat array tagged with type: "individual" |
                // "collaborator"/"collaboration" — not pre-split into buckets.
                const all = Array.isArray(data.data) ? data.data : []
                const collabs = all.filter((w) => w.type !== 'individual')
                const individual = all.find((w) => w.type === 'individual')
                setCollaborations(collabs)
                setIndividualId(individual?.id ?? null)
                if (collabs.length === 0) {
                    await saveToWishlist(individual?.id)
                } else {
                    setSelected('individual')
                    setOpen(true)
                }
            } catch {
                setCollaborations([])
                setSaved(true)
            } finally {
                setLoading(false)
            }
            return
        }

        if (collaborations.length === 0) {
            await saveToWishlist(individualId)
            return
        }
        setSelected('individual')
        setOpen(true)
    }

    const handleConfirm = async () => {
        const targetId = selected === 'individual' ? individualId : selected
        await saveToWishlist(targetId)
        setOpen(false)
    }

    return (
        <>
            <button
                type='button'
                className={`${className} ${saved ? 'is-saved' : ''}`}
                aria-label={saved ? 'Remove from wishlist' : 'Save to wishlist'}
                onClick={handleClick}
                disabled={loading || saving}
            >
                <img src={saved ? Heart : WishlistWhite} alt="" />
            </button>

            <Modal open={open} onClose={() => setOpen(false)} maxWidth={380} className='saveWishlistModal'>
                <h2 className='saveWishlistModal__title'>Save to Wishlist</h2>
                <p className='saveWishlistModal__subtitle'>Choose where you'd like to save this</p>

                <div className='saveWishlistModal__options'>
                    <label className={`saveWishlistModal__option ${selected === 'individual' ? 'active' : ''}`}>
                        <input
                            type='radio'
                            name='saveWishlist'
                            checked={selected === 'individual'}
                            onChange={() => setSelected('individual')}
                        />
                        My Wishlist
                    </label>
                    {collaborations?.map((wishlist) => (
                        <label
                            key={wishlist.id}
                            className={`saveWishlistModal__option ${selected === wishlist.id ? 'active' : ''}`}
                        >
                            <input
                                type='radio'
                                name='saveWishlist'
                                checked={selected === wishlist.id}
                                onChange={() => setSelected(wishlist.id)}
                            />
                            {wishlist.name}
                        </label>
                    ))}
                </div>

                <button type='button' className='commonBtn w-100 mt-4' onClick={handleConfirm} disabled={saving}>
                    {saving ? 'Adding...' : 'Add'}
                </button>
            </Modal>
        </>
    )
}

export default SaveToWishlistButton
